/**
 * Layer 1 - note judgment: how good was one note?
 *
 * Each judged dimension has its own credit curve. "Not judged" is a real exclusion, not a flat
 * curve: an excluded dimension never pads the combiner. The merge rule has two parts: a gate per
 * dimension (if a gated dimension fails, the note scores 0) and a combiner over the dimensions
 * that are not gated (worst-of, product, or weighted average). The combiner re-normalizes over
 * whatever is left, so removing a dimension (a mask, the pedal rule, order matching) never
 * counts against the note.
 */

import {
  CreditCurve,
  CurveUnit,
  clampCurveToLength,
  creditAt,
  creditCurveErrors,
  sideUnits,
} from './creditCurve';
import { DIMENSIONS, Dimension, GradingConfigError, TIMING_DIMENSIONS } from './types';

export interface DimensionGate {
  /** The dimension passes at or above this credit; below it, the whole note scores 0. */
  readonly passAt: number;
}

export interface DimensionRule {
  readonly curve: CreditCurve;
  /** When set, this dimension is a gate and does not enter the combiner. */
  readonly gate?: DimensionGate;
  /** Weight under the weighted-average combiner (default 1). */
  readonly weight?: number;
}

export type Combiner = 'worst-of' | 'product' | 'weighted-average';

export interface NoteJudgmentConfig {
  /** The judged dimensions. A dimension that is absent is not judged. */
  readonly dimensions: Readonly<Partial<Record<Dimension, DimensionRule>>>;
  readonly combiner: Combiner;
}

export type NotJudgedReason =
  /** The preset does not judge it. */
  | 'preset'
  /** Removed by the input's capability mask or by live narrowing. */
  | 'removed'
  /** Onset and offset need a clock; order matching has none. */
  | 'order-matching'
  /** The key was released while the sustain pedal was down. */
  | 'pedal'
  /** The score gives no target for it (no dynamic, no fingering). */
  | 'no-target'
  /** Order matching without a set reference tempo cannot turn notated length into time. */
  | 'no-reference-tempo'
  /** The press carried no reading for it (e.g. no finger tracked). */
  | 'no-reading';

/** A dimension that was judged from a reading. */
export interface JudgedOutcome {
  readonly state: 'judged';
  readonly deviation: number;
  readonly credit: number;
  /**
   * True while the reading may still change: a key held past its release window reads as late
   * now, but a release under the sustain pedal later would suspend it.
   */
  readonly provisional?: boolean;
}

export type DimensionOutcome =
  | JudgedOutcome
  | { readonly state: 'pending' }
  | { readonly state: 'not-judged'; readonly reason: NotJudgedReason };

export type DimensionOutcomes = Readonly<Partial<Record<Dimension, DimensionOutcome>>>;

export interface MergedNote {
  /** False when a gated dimension failed. */
  readonly gatePassed: boolean;
  /** The note's credit, 0 to 1. Provisional while `settled` is false. */
  readonly credit: number;
  /** True once no judged dimension is still waiting for data. */
  readonly settled: boolean;
}

const ALLOWED_UNITS: Readonly<Record<Dimension, readonly CurveUnit[]>> = {
  pitch: ['semitones'],
  onset: ['ms', 'beats'],
  offset: ['ms', 'beats', 'proportion'],
  duration: ['ms', 'beats', 'proportion'],
  velocity: ['velocity'],
  finger: ['fingers'],
};

/** A length curve moves a release edge with the note's length; onset has no such edge. */
const LENGTH_CURVE_DIMENSIONS: readonly Dimension[] = ['offset', 'duration'];

/** A curve's units must fit its dimension: the shared unit and each side's own. */
function curveUnitErrors(dimension: Dimension, curve: CreditCurve): string[] {
  const allowed = ALLOWED_UNITS[dimension];
  const units = [curve.unit, ...sideUnits(curve)].filter(
    (unit, index, all) => all.indexOf(unit) === index,
  );
  const unitErrors = units
    .filter((unit) => allowed.indexOf(unit) < 0)
    .map((unit) => `${dimension}: unit ${unit} is not allowed (use ${allowed.join(' / ')})`);
  const lengthCurveErrors =
    LENGTH_CURVE_DIMENSIONS.indexOf(dimension) < 0 &&
    (curve.sides?.below?.edgeByLength !== undefined ||
      curve.sides?.above?.edgeByLength !== undefined)
      ? [`${dimension}: a length curve is only for offset and duration`]
      : [];
  return [...unitErrors, ...lengthCurveErrors];
}

export function judgedDimensions(config: NoteJudgmentConfig): Dimension[] {
  return DIMENSIONS.filter((dimension) => config.dimensions[dimension] !== undefined);
}

/** At most two of onset, offset and duration may be graded: the third is their shadow. */
export function timingDimensionErrors(config: NoteJudgmentConfig): string[] {
  const timing = judgedDimensions(config).filter(
    (dimension) => TIMING_DIMENSIONS.indexOf(dimension) >= 0,
  );
  return timing.length > 2
    ? [
        `at most two of onset, offset and duration may be graded (the third is their shadow); got ${timing.join(', ')}`,
      ]
    : [];
}

/** Every validity problem with a note-judgment config; empty when valid. */
export function noteJudgmentErrors(config: NoteJudgmentConfig): string[] {
  const judged = judgedDimensions(config);
  const timingErrors = timingDimensionErrors(config);
  const dimensionErrors = judged.reduce<string[]>((errors, dimension) => {
    const rule = config.dimensions[dimension] as DimensionRule;
    const unitErrors = curveUnitErrors(dimension, rule.curve);
    const curveErrors = creditCurveErrors(rule.curve).map((error) => `${dimension}: ${error}`);
    const gateErrors =
      rule.gate && !(rule.gate.passAt > 0 && rule.gate.passAt <= 1)
        ? [`${dimension}: a gate's passAt must be above 0 and at most 1`]
        : [];
    const weightErrors =
      rule.weight !== undefined && !(rule.weight > 0)
        ? [`${dimension}: weights must be positive`]
        : [];
    return [...errors, ...unitErrors, ...curveErrors, ...gateErrors, ...weightErrors];
  }, []);
  return [...timingErrors, ...dimensionErrors];
}

export function validateNoteJudgment(config: NoteJudgmentConfig): void {
  const errors = noteJudgmentErrors(config);
  if (errors.length > 0) {
    throw new GradingConfigError(`note judgment: ${errors.join('; ')}`);
  }
}

/**
 * The config with the given dimensions removed. Used by the capability mask and live
 * narrowing alike: it can only ever take dimensions away, never add or loosen one.
 */
export function withoutDimensions(
  config: NoteJudgmentConfig,
  removed: readonly Dimension[],
): NoteJudgmentConfig {
  const dimensions = judgedDimensions(config)
    .filter((dimension) => removed.indexOf(dimension) < 0)
    .reduce<Partial<Record<Dimension, DimensionRule>>>(
      (kept, dimension) => ({ ...kept, [dimension]: config.dimensions[dimension] }),
      {},
    );
  return { ...config, dimensions };
}

/** Judges one dimension from its deviation, cutting the window at the note's length when given. */
export function judgeDimension(
  rule: DimensionRule,
  deviation: number,
  noteLengthInUnit?: number,
): JudgedOutcome {
  const curve =
    noteLengthInUnit === undefined ? rule.curve : clampCurveToLength(rule.curve, noteLengthInUnit);
  return { state: 'judged', deviation, credit: creditAt(curve, deviation) };
}

function combine(
  combiner: Combiner,
  entries: readonly { readonly credit: number; readonly weight: number }[],
): number {
  if (entries.length === 0) {
    return 1;
  }
  if (combiner === 'worst-of') {
    return Math.min(...entries.map((entry) => entry.credit));
  }
  if (combiner === 'product') {
    return entries.reduce((product, entry) => product * entry.credit, 1);
  }
  const totalWeight = entries.reduce((sum, entry) => sum + entry.weight, 0);
  return entries.reduce((sum, entry) => sum + entry.credit * entry.weight, 0) / totalWeight;
}

/** Merges a note's dimension outcomes into one credit: the gates first, then the combiner. */
export function mergeNote(config: NoteJudgmentConfig, outcomes: DimensionOutcomes): MergedNote {
  const judged = judgedDimensions(config);
  const settled = judged.every((dimension) => {
    const outcome = outcomes[dimension];
    return (
      outcome?.state !== 'pending' && !(outcome?.state === 'judged' && outcome.provisional === true)
    );
  });
  const failedGates = judged
    .filter((dimension) => {
      const rule = config.dimensions[dimension] as DimensionRule;
      const outcome = outcomes[dimension];
      return (
        rule.gate !== undefined && outcome?.state === 'judged' && outcome.credit < rule.gate.passAt
      );
    })
    .map((dimension) => outcomes[dimension] as JudgedOutcome);
  if (failedGates.length > 0) {
    // A provisional failure (a key held past its window) may still be suspended by the pedal.
    const settledFailure = failedGates.some((outcome) => outcome.provisional !== true);
    return { gatePassed: false, credit: 0, settled: settledFailure };
  }
  const entries = judged
    .filter((dimension) => (config.dimensions[dimension] as DimensionRule).gate === undefined)
    .reduce<{ credit: number; weight: number }[]>((kept, dimension) => {
      const outcome = outcomes[dimension];
      if (outcome?.state !== 'judged') {
        return kept;
      }
      const weight = (config.dimensions[dimension] as DimensionRule).weight ?? 1;
      return [...kept, { credit: outcome.credit, weight }];
    }, []);
  return { gatePassed: true, credit: combine(config.combiner, entries), settled };
}
